import React from "react";
import { Button, Modal } from "../../../components/ui";
// The same mark the Plans tab shows over its own empty list — see
// `BrandPlansTab`. Deliberately the same one: this dialog is about that
// screen, and arriving there to find a different drawing over the same absence
// would read as a different subject. It carries its own tinted blob at 56px,
// so it needs no wrapper here.
import { ReactComponent as FirstPlanMark } from "../../../assets/svg/No data images/plans.svg";

export interface PlansRequiredModalProps {
  /** Takes them to the Plans tab of My brand, which is where one is made. */
  onCreatePlan: () => void;
  /** The way out. Escape lands here too. */
  onDismiss: () => void;
  /**
   * What the way out reads as. "Not now" where they are being offered a detour
   * off a screen they can stay on; the screen behind a dialog they cannot use
   * without a plan passes its own way back instead — "Back to my clients".
   */
  dismissLabel?: string;
}

/**
 * Raised when somebody reaches for Add client or New enquiry on a workspace
 * that does not sell anything yet.
 *
 * A dialog rather than a greyed button, and deliberately so: this is not a door
 * that is shut — the way through it is one screen away and takes a minute — so
 * the useful thing is to say what is missing and hand over the way to fix it.
 * A disabled button with a tooltip says the first half and leaves them to find
 * the second, which on this app means guessing that plans live under My
 * brand. The subscription gate greys its buttons because nothing on that road
 * is a minute's work; this one is.
 *
 * The copy is written as the next step rather than as a refusal. Nothing has
 * gone wrong here — a workspace on its first day has no plans yet, which is
 * the expected state and not a mistake anybody made — so there is no red, no
 * warning triangle, and no "cannot". What it says is what a plan is for and
 * what happens once there is one.
 *
 * Not dismissable by a click beside it, for the reason `Modal` gives: this is a
 * question with two real answers under it, and a stray click is far likelier to
 * be a miss than a decision. Escape still closes it, and lands on `onDismiss`.
 */
const PlansRequiredModal: React.FC<PlansRequiredModalProps> = ({
  onCreatePlan,
  onDismiss,
  dismissLabel = "Not now",
}) => (
  <Modal
    open
    // Names the step rather than the obstacle. It is also the dialog's
    // accessible name, which is why it reads as a sentence on its own.
    title="Add your first plan"
    onClose={onDismiss}
    dismissOnBackdrop={false}
    // The heading is drawn in the body under the mark, so the panel reads as
    // one block rather than as a bar with a message beneath it. The `h2` stays
    // in the tree off screen as the dialog's name — see `Modal`'s `hideTitle`.
    hideTitle
    className="max-w-[460px]"
    footer={
      <>
        <Button variant="secondary" size="md" onClick={onDismiss}>
          {dismissLabel}
        </Button>
        {/* The one thing the dialog is for, and the reason it is a dialog at
            all: it does not just say what is missing, it goes and gets it.
            The same words the Plans tab's own empty state puts on the same
            action, so the button pressed here and the one waiting there read
            as the one step. */}
        <Button size="md" onClick={onCreatePlan}>
          Create Plan
        </Button>
      </>
    }
  >
    <div className="flex flex-col items-center text-center">
      {/* Decorative: the heading under it already says what this is.
          Drawn under its natural 56px — this is a mark over two lines of copy
          in a 460px panel, not the illustration over an empty screen that the
          Plans tab draws it as. */}
      <FirstPlanMark aria-hidden className="h-12 w-12" />

      <h3
        aria-hidden
        className="mt-4 text-[17px] 3xl:text-[19px] font-medium text-ink"
      >
        Add your first plan
      </h3>

      <p className="mt-2 text-[14px] 3xl:text-[15px] leading-6 text-ink-muted">
        Create a plan for your clients to sign up for. You can add more or
        edit them anytime.
      </p>
    </div>
  </Modal>
);

export default PlansRequiredModal;
